// =============================================================================
// Tryb shadow silnika deterministycznego (legacy UI): po starcie generacji wykonuje
// DODATKOWE, niewidoczne wywołanie POST /api/engine/analyze ze strukturalnymi wynikami,
// wiekiem w miesiącach (liczonym TU, w przeglądarce) i wynikami legacy. Imię i data
// urodzenia NIGDY nie opuszczają przeglądarki w tym wywołaniu. Błędy są ignorowane.
// Tryb (off|shadow|on) przychodzi z GET /api/engine/status — nie ze zmiennej Vite.
// =============================================================================
import { API_BASE_URL } from '../config';
import { calculateDiscrepancies, getScoreInterpretation } from '../components/SimpleWISCV/utils/helpers';
import type { PatientInfo } from '../components/SimpleWISCV/types';

const COMPOSITE_KEYS = ['vci', 'vsi', 'fri', 'wmi', 'psi', 'fsiq'] as const;
const SUBTEST_KEYS = [
  'block_design', 'similarities', 'matrix_reasoning', 'digit_span', 'coding',
  'vocabulary', 'figure_weights', 'visual_puzzles', 'picture_span', 'symbol_search',
] as const;

export interface ShadowRequest {
  age_months: number;
  indices: Record<string, number>;
  subtests: Record<string, number>;
  legacy: {
    pairs: Array<{ a: string; b: string; isSignificant: boolean }>;
    classes: Record<string, string>;
  };
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Pełne miesiące między datami YYYY-MM-DD; null przy błędnych/odwróconych datach. */
export function computeAgeMonths(birthDate: string, testDate: string): number | null {
  const b = DATE_RE.exec(birthDate);
  const t = DATE_RE.exec(testDate);
  if (!b || !t) return null;
  const [by, bm, bd] = [Number(b[1]), Number(b[2]), Number(b[3])];
  const [ty, tm, td] = [Number(t[1]), Number(t[2]), Number(t[3])];
  let months = (ty - by) * 12 + (tm - bm);
  if (td < bd) months -= 1;
  return months >= 0 ? months : null;
}

const toInt = (v: string | undefined): number | null => {
  if (v === undefined || v.trim() === '') return null;
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
};

export function buildShadowRequest(
  patient: Pick<PatientInfo, 'birthDate' | 'testDate'>,
  compositeScores: Record<string, string>,
  subtestScores: Record<string, string>,
): ShadowRequest | null {
  const testDate = patient.testDate || new Date().toISOString().slice(0, 10);
  const age = computeAgeMonths(patient.birthDate, testDate);
  if (age === null) return null;

  const indices: Record<string, number> = {};
  for (const k of COMPOSITE_KEYS) {
    const n = toInt(compositeScores[k]);
    if (n !== null) indices[k.toUpperCase()] = n;
  }
  if (Object.keys(indices).length === 0) return null;

  const subtests: Record<string, number> = {};
  for (const k of SUBTEST_KEYS) {
    const n = toInt(subtestScores[k]);
    if (n !== null) subtests[k] = n;
  }

  const classes: Record<string, string> = {};
  for (const k of COMPOSITE_KEYS) {
    const label = getScoreInterpretation(compositeScores[k] ?? '', 'composite');
    if (label) classes[k] = label;
  }
  const pairs = calculateDiscrepancies(compositeScores).map((d) => ({ a: d.domainA, b: d.domainB, isSignificant: d.isSignificant }));

  return { age_months: age, indices, subtests, legacy: { pairs, classes } };
}

let modePromise: Promise<'off' | 'shadow' | 'on'> | null = null;

async function fetchMode(): Promise<'off' | 'shadow' | 'on'> {
  try {
    const r = await fetch(`${API_BASE_URL}/engine/status`);
    if (!r.ok) return 'off';
    const j = (await r.json()) as { mode?: string };
    return j.mode === 'shadow' || j.mode === 'on' ? j.mode : 'off';
  } catch {
    return 'off';
  }
}

/** Test-only: czyści cache trybu. */
export function __resetEngineShadowForTests(): void {
  modePromise = null;
}

/** Niewidoczne wywołanie shadow. Nigdy nie rzuca i niczego nie zmienia w UI. */
export async function runEngineShadow(
  patient: Pick<PatientInfo, 'birthDate' | 'testDate'>,
  compositeScores: Record<string, string>,
  subtestScores: Record<string, string>,
): Promise<void> {
  try {
    modePromise ??= fetchMode();
    if ((await modePromise) !== 'shadow') return;
    const body = buildShadowRequest(patient, compositeScores, subtestScores);
    if (!body) return;
    await fetch(`${API_BASE_URL}/engine/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // shadow nigdy nie wpływa na użytkownika
  }
}
